import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import "./affiliate-program.css";

const AffiliateProgramPage = () => {
    // Accordion state for FAQs
    const [openFaqIndex, setOpenFaqIndex] = useState(0);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const toggleFaq = (index) => {
        setOpenFaqIndex((prevIndex) => (prevIndex === index ? null : index));
    };

    const faqItems = [
        {
            question: "What is the AGCX Affiliate Program?",
            answer:
                "The AGCX Affiliate Program allows you to earn commissions by referring new users to the platform. You earn a percentage of their trading fees for as long as they trade on AGCX.",
        },
        {
            question: "How do I join the AGCX Affiliate Program?",
            answer:
                "Simply click on the 'Become an Affiliate' button, complete the quick registration form, and get your unique referral link to start sharing with your audience.",
        },
        {
            question: "How much commission can I earn on AGCX?",
            answer:
                "You can earn up to 60% commission on trading fees generated by your referred users with no upper limit or cap on your total earnings.",
        },
        {
            question: "When and how do I get paid?",
            answer:
                "Commissions are calculated and paid out daily directly to your AGCX account wallet in crypto.",
        },
        {
            question: "Is there a minimum payout threshold?",
            answer:
                "No, there is no minimum payout threshold. All earned commissions are credited automatically to your account.",
        },
    ];

    return (
        <>
            <Helmet>
                <title>AGCX Affiliate Program | Earn Up to 60% Commission</title>
                <meta
                    name="description"
                    content="Join the AGCX Affiliate Program and earn up to 60% lifetime commission on trading fees by referring traders to our global exchange."
                />
                <meta
                    name="keywords"
                    content="crypto affiliate, AGCX affiliate program, earn crypto commissions, referral rewards"
                />
            </Helmet>

            <div
                className="affiliate-page"
                style={{
                    backgroundImage: `url("${process.env.PUBLIC_URL || ''}/images/affiliate-program%20-img/affiliate_program_bg.png")`,
                    backgroundSize: "containt",
                    backgroundPosition: "top center",
                    backgroundRepeat: "no-repeat"
                }}
            >
                <div className="affiliate-container">

                    {/* ========================================== */}
                    {/* --- 1. HERO SECTION --- */}
                    {/* ========================================== */}
                    <div className="affiliate-hero">
                        <div className="affiliate-hero-left">
                            <h1 className="affiliate-hero-title">
                                Earn More. <br />
                                <span className="gold-text">Together with AGCX</span>
                            </h1>
                            <p className="affiliate-hero-desc">
                                Join the AGCX Affiliate Program and earn up to 50% commission by referring traders to our high-liquidity platform for growth and global exposure.
                            </p>
                            <div className="affiliate-hero-btns">
                                <Link to="/signup" className="btn-primary-gold">
                                    Become an Affiliate
                                </Link>
                                <a href="#how-it-works" className="btn-secondary-light">
                                    Learn More <i className="ri-arrow-right-s-line"></i>
                                </a>
                            </div>

                            {/* Metric Stat Row Below Buttons */}
                            <div className="affiliate-metrics-row">
                                {/* Metric 1 */}
                                <div className="hero-stat-item">
                                    <div className="hero-stat-icon">
                                        <img src="/images/affiliate-program -img/earn_more_icon.svg" alt="Affiliates" />
                                    </div>
                                    <span className="hero-stat-value">15,000+</span>
                                    <span className="hero-stat-label">Affiliates</span>
                                </div>

                                {/* Metric 2 */}
                                <div className="hero-stat-item">
                                    <div className="hero-stat-icon">
                                        <img src="/images/affiliate-program -img/earn_more_icon2.svg" alt="Monthly Volume" />
                                    </div>
                                    <span className="hero-stat-value">100,000,000+</span>
                                    <span className="hero-stat-label">Monthly Volume</span>
                                </div>

                                {/* Metric 3 */}
                                <div className="hero-stat-item">
                                    <div className="hero-stat-icon">
                                        <img src="/images/affiliate-program -img/earn_more_icon3.svg" alt="Countries" />
                                    </div>
                                    <span className="hero-stat-value">200+</span>
                                    <span className="hero-stat-label">Countries</span>
                                </div>

                                {/* Metric 4 */}
                                <div className="hero-stat-item">
                                    <div className="hero-stat-icon">
                                        <img src="/images/affiliate-program -img/earn_more_icon4.svg" alt="Max Commission Rate" />
                                    </div>
                                    <span className="hero-stat-value">60%</span>
                                    <span className="hero-stat-label">Max Commission Rate</span>
                                </div>
                            </div>
                        </div>

                        {/* Hero Right Graphic */}
                        <div className="affiliate-hero-right">
                            <img
                                src="/images/affiliate-program -img/affiliate_program_img.png"
                                alt="Earn More Together with AGCX"
                            />
                        </div>
                    </div>

                </div>


                {/* ========================================== */}
                {/* --- 2. HOW TO EARN COMMISSIONS SECTION --- */}
                {/* ========================================== */}
                <div className="affiliate-section" id="how-it-works">
                    <div className="affiliate-container">
                        <div className="section-header-center2">
                            <h2>How to Earn Commissions</h2>
                        </div>

                        <div className="steps-grid">
                            {/* Step 1 */}
                            <div className="step-card2">
                                <span className="step-num-badge">01</span>
                                <div className="step-card-icon">
                                    <img src="/images/affiliate-program -img/earn_commissions_icon.png" alt="Apply" />
                                </div>
                                <h3>Apply</h3>
                                <p>Create your affiliate account and get access to your unique referral links and dashboard.</p>
                            </div>

                            {/* Step 2 */}
                            <div className="step-card2">
                                <span className="step-num-badge">02</span>
                                <div className="step-card-icon">
                                    <img src="/images/affiliate-program -img/earn_commissions_icon2.png" alt="Promote" />
                                </div>
                                <h3>Promote</h3>
                                <p>Share your referral link with your audience through social media, blogs, and communities.</p>
                            </div>

                            {/* Step 3 */}
                            <div className="step-card2">
                                <span className="step-num-badge">03</span>
                                <div className="step-card-icon">
                                    <img src="/images/affiliate-program -img/earn_commissions_icon3.png" alt="Earn" />
                                </div>
                                <h3>Earn</h3>
                                <p>Earn commissions every time your referrals trade on AGCX. Get paid instantly.</p>
                            </div>
                        </div>

                    </div>
                </div>


                {/* ========================================== */}
                {/* --- 3. COMMISSION BANNER SECTION --- */}
                {/* ========================================== */}
                <div className="affiliate-container">
                    <div className="commission-banner">

                        <div className="commission-banner-content">
                            <span className="commission-subtitle">Top Earners</span>
                            <h2 className="commission-title">Earn up to 60% Commission</h2>
                            <p className="commission-desc">
                                Refer friends to trade on AGCX and earn a percentage of their trading fees every day. The more you refer, the more you earn — with no cap on your earnings.
                            </p>
                            <Link to="/signup" className="btn-primary-gold2">
                                Start Earning <i className="ri-arrow-right-s-line"></i>
                            </Link>
                        </div>
                        <div className="commission-banner-image">
                            <img src="/images/affiliate-program -img/earn_gift_img.png" alt="Earn 60% Commission" />
                        </div>

                    </div>
                </div>


                {/* ========================================== */}
                {/* --- 4. WHY BECOME AN AGCX AFFILIATE SECTION --- */}
                {/* ========================================== */}
                <div className="affiliate-section">
                    <div className="affiliate-container">

                        <div className="section-header-center2">
                            <h2>Why Become an AGCX Affiliate?</h2>
                        </div>

                        <div className="benefits-grid">
                            {/* Feature 1 */}
                            <div className="benefit-card">
                                <div className="benefit-icon">
                                    <img src="/images/affiliate-program -img/agce_affiliate_icon.png" alt="High Commissions" />
                                </div>
                                <h3>High Commissions</h3>
                                <p>Earn up to 60% commission on every trade your referrals make on the AGCX platform.</p>
                            </div>

                            {/* Feature 2 */}
                            <div className="benefit-card">
                                <div className="benefit-icon">
                                    <img src="/images/affiliate-program -img/agce_affiliate_icon2.png" alt="Transparent System" />
                                </div>
                                <h3>Transparent System</h3>
                                <p>Track all your referrals, earnings, and commissions in real-time on your affiliate dashboard.</p>
                            </div>

                            {/* Feature 3 */}
                            <div className="benefit-card">
                                <div className="benefit-icon">
                                    <img src="/images/affiliate-program -img/agce_affiliate_icon3.png" alt="Premium Brands" />
                                </div>
                                <h3>Premium Brands</h3>
                                <p>Partner with one of the most trusted and recognized crypto exchange brands globally.</p>
                            </div>

                            {/* Feature 4 */}
                            <div className="benefit-card">
                                <div className="benefit-icon">
                                    <img src="/images/affiliate-program -img/agce_affiliate_icon4.png" alt="Multi-Tier Rewards" />
                                </div>
                                <h3>Multi-Tier Rewards</h3>
                                <p>Unlock additional bonuses and rewards as you grow your referral network over time.</p>
                            </div>
                        </div>

                    </div>
                </div>


                {/* ========================================== */}
                {/* --- 5. FREQUENTLY ASKED QUESTIONS SECTION --- */}
                {/* ========================================== */}
                <div className="faq-section">

                    <div className="affiliate-container">

                        <div className="section-header-center2">
                            <h2>Frequently Asked Questions</h2>
                        </div>

                        <div className="faq-accordion">
                            {faqItems.map((item, index) => (
                                <div
                                    key={index}
                                    className={`faq-item ${openFaqIndex === index ? "active" : ""}`}
                                >
                                    <button
                                        className="faq-question"
                                        onClick={() => toggleFaq(index)}
                                    >
                                        <span>{item.question}</span>
                                        <i className="ri-arrow-down-s-line faq-chevron"></i>
                                    </button>
                                    {openFaqIndex === index && (
                                        <div className="faq-answer">
                                            <p>{item.answer}</p>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>

                    </div>
                </div>


            </div>
        </>
    );
};

export default AffiliateProgramPage;
